import styled, { keyframes } from "styled-components";

/* ═══════════════════════════════════════════════════════════════════════
   LegalPage shared styles — Light Water redesign
   Used by PrivacyPage + TermsPage. Frosted-white shell with aqua accents,
   gradient hero title, light frosted sections, brand-aqua bullets and
   callouts. All original named exports preserved so JS layer stays intact.
   ═══════════════════════════════════════════════════════════════════════ */

const panelFloat = keyframes`
  0%, 100% { filter: brightness(1); }
  50%      { filter: brightness(1.02); }
`;

// ─── Page wrapper ───────────────────────────────────────────────────────────

export const PageWrapper = styled.div`
  width: 100%;
  max-width: 880px;
  margin: 0 auto;
  padding: 24px 20px 64px;
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-weight: 400;
  line-height: normal;

  @media (max-width: 640px) {
    padding: 16px 12px 48px;
  }
`;

// ─── Hero header ────────────────────────────────────────────────────────────

export const Hero = styled.header`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 32px 30px 28px;
  margin-bottom: 18px;
  border-radius: 28px;
  border: 1px solid var(--glass-edge);
  background:
    radial-gradient(
      120% 60% at 30% -10%,
      rgba(var(--glass-white-rgb), 0.96) 0%,
      rgba(var(--glass-pale-rgb), 0.9) 55%,
      rgba(var(--glass-pale-rgb), 0.82) 100%
    ),
    linear-gradient(
      180deg,
      rgba(var(--glass-white-rgb), 0.94) 0%,
      rgba(var(--glass-pale-rgb), 0.9) 100%
    );
  backdrop-filter: blur(28px) saturate(180%);
  -webkit-backdrop-filter: blur(28px) saturate(180%);
  box-shadow:
    0 32px 64px -18px rgba(var(--color-brand-500-rgb), 0.32),
    0 12px 28px rgba(var(--color-brand-400-rgb), 0.18),
    inset 0 2px 0 rgba(255, 255, 255, 0.95),
    inset 0 -10px 22px rgba(var(--color-brand-200-rgb), 0.22);

  [data-theme="dark"] & {
    border-color: transparent;
    box-shadow:
      0 24px 48px -18px rgba(0, 0, 0, 0.55),
      0 8px 18px rgba(0, 0, 0, 0.32),
      inset 0 -10px 22px rgba(var(--color-brand-700-rgb), 0.16);
  }

  [data-theme="dark"] &::after {
    opacity: 0.35;
  }
  isolation: isolate;
  overflow: hidden;
  animation: ${panelFloat} 6s ease-in-out infinite;

  /* Top specular sheen */
  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 32px;
    right: 32px;
    height: 20px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.9) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(2px);
    pointer-events: none;
    z-index: 1;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  /* Soft aqua bloom on the right edge */
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
      circle at 90% -10%,
      rgba(var(--color-brand-400-rgb), 0.26),
      transparent 55%
    );
    pointer-events: none;
    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 2;
  }

  @media (max-width: 640px) {
    padding: 24px 22px 22px;
    border-radius: 22px;
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

export const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  padding: 5px 12px;
  border-radius: 999px;
  border: 1px solid var(--color-line);
  background: var(--color-brand-mist);
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  box-shadow: var(--shadow-micro);

  /* Dark theme — brand-mist + brand-700 = blue-on-blue, unreadable.
     Switch to subtle dark surface + bright text. */
  [data-theme="dark"] & {
    border-color: transparent;
    background: rgba(var(--color-brand-400-rgb), 0.18);
    color: var(--color-text-strong);
  }
`;

export const Title = styled.h1`
  margin: 0;
  font-family: var(--font-family-primary);
  font-size: 36px;
  font-weight: 800;
  line-height: normal;
  letter-spacing: -0.03em;

  /* Brand gradient title */
  background: linear-gradient(
    0deg,
    var(--color-text-strong) 0%,
    var(--color-text-body) 45%,
    var(--color-brand-600) 100%
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  filter: drop-shadow(0 1px 2px rgba(var(--color-brand-600-rgb), 0.14));

  @media (max-width: 640px) {
    font-size: 28px;
  }
`;

export const LastUpdated = styled.p`
  margin: 0;
  font-family: var(--font-family-primary);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-text-muted);
`;

// ─── Sections ───────────────────────────────────────────────────────────────

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const Introduction = styled.p`
  margin: 0;
  padding: 24px 26px;
  border-radius: 22px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-2);
  backdrop-filter: blur(18px) saturate(160%);
  -webkit-backdrop-filter: blur(18px) saturate(160%);
  box-shadow:
    var(--shadow-micro),
    inset 0 1px 0 rgba(255, 255, 255, 0.95);
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: 15px;
  font-weight: 500;
  line-height: normal;

  [data-theme="dark"] & {
    border-color: transparent;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  }

  @media (max-width: 640px) {
    padding: 20px 20px;
    border-radius: 18px;
    font-size: 14px;
  }
`;

export const Section = styled.section`
  padding: 26px 28px;
  border-radius: 22px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-1);
  backdrop-filter: blur(18px) saturate(160%);
  -webkit-backdrop-filter: blur(18px) saturate(160%);
  box-shadow: var(--shadow-micro);
  transition:
    border-color 220ms var(--ease-snappy),
    transform 220ms var(--ease-snappy);

  [data-theme="dark"] & {
    border-color: transparent;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  }

  &:hover {
    border-color: var(--color-line-strong);
    transform: translateY(-1px);
  }

  [data-theme="dark"] &:hover {
    border-color: transparent;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.5);
  }

  @media (max-width: 640px) {
    padding: 22px 20px;
    border-radius: 18px;
  }
`;

export const SectionTitle = styled.h2`
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin: 0 0 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--color-line);
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.02em;
`;

export const SubSection = styled.div`
  margin: 18px 0 0;
  padding-left: 16px;
  border-left: 2px solid var(--color-line-strong);
`;

export const SubSectionTitle = styled.h3`
  margin: 0 0 8px;
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 15px;
  font-weight: 800;
  letter-spacing: -0.01em;
`;

export const SectionContent = styled.div`
  margin: 0;
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: 14.5px;
  font-weight: 400;
  line-height: normal;

  strong {
    color: var(--color-text-strong);
    font-weight: 700;
  }
`;

// ─── Lists ──────────────────────────────────────────────────────────────────

export const BulletList = styled.div`
  margin: 12px 0;
`;

export const BulletPoint = styled.div`
  position: relative;
  margin: 8px 0;
  padding-left: 22px;
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: 14px;
  line-height: normal;

  &::before {
    content: "";
    position: absolute;
    left: 4px;
    top: 9px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: linear-gradient(
      180deg,
      var(--color-brand-300) 0%,
      var(--color-brand-500) 100%
    );
    box-shadow:
      0 2px 6px rgba(var(--color-brand-500-rgb), 0.32),
      inset 0 1px 0 rgba(255, 255, 255, 0.55);
  }

  strong {
    color: var(--color-text-strong);
    font-weight: 700;
  }
`;

// ─── Tables (Privacy) ───────────────────────────────────────────────────────

export const Table = styled.div`
  margin: 16px 0 6px;
  border: 1px solid var(--color-line);
  border-radius: 18px;
  overflow: hidden;
  background: var(--color-surface-2);
  box-shadow:
    var(--shadow-micro),
    inset 0 1px 0 rgba(255, 255, 255, 0.95);

  [data-theme="dark"] & {
    border-color: transparent;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  }
`;

export const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  background: var(--color-brand-mist);
  border-bottom: 1px solid var(--color-line);

  [data-theme="dark"] & {
    background: rgba(var(--color-brand-400-rgb), 0.18);
  }
`;

export const TableHeaderCell = styled.div`
  padding: 14px 16px;
  font-family: var(--font-family-primary);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-brand-700);

  [data-theme="dark"] & {
    color: var(--color-text-strong);
  }

  &:first-child {
    border-right: 1px solid var(--color-line);
  }
`;

export const TableRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  border-bottom: 1px solid var(--color-line);

  &:last-child {
    border-bottom: none;
  }
`;

export const TableCell = styled.div`
  padding: 14px 16px;
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 400;
  line-height: normal;
  color: var(--color-text-body);

  strong {
    color: var(--color-text-strong);
    font-weight: 700;
  }

  &:first-child {
    border-right: 1px solid var(--color-line);
  }
`;

// ─── Highlight blocks ───────────────────────────────────────────────────────

export const ImportantNote = styled.div`
  margin: 14px 0;
  padding: 16px 18px;
  border-radius: 16px;
  border: 1px solid var(--color-line);
  border-left: 3px solid var(--color-brand-500);
  background: var(--color-brand-mist);
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
  box-shadow: var(--shadow-micro);

  [data-theme="dark"] & {
    border-color: transparent;
    border-left: 3px solid var(--color-brand-500);
    background: rgba(var(--color-brand-400-rgb), 0.14);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.4);
  }

  strong {
    color: var(--color-brand-700);
    font-weight: 800;
  }

  [data-theme="dark"] & strong {
    color: var(--color-text-strong);
  }
`;

export const HighlightText = styled.div`
  margin: 14px 0 0;
  padding: 14px 18px;
  border-radius: 16px;
  border: 1px solid var(--glass-edge-soft);
  background: linear-gradient(
    180deg,
    var(--color-brand-300) 0%,
    var(--color-brand-500) 100%
  );
  color: var(--glass-text-on-pill);
  text-shadow: 0 1px 1px rgba(var(--color-brand-900-rgb), 0.22);
  box-shadow:
    var(--shadow-cta),
    inset 0 1px 0 rgba(255, 255, 255, 0.55);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 800;
  letter-spacing: -0.005em;
  text-align: center;

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

export const EmailLink = styled.a`
  color: var(--color-brand-700);
  text-decoration: none;
  font-weight: 700;
  border-bottom: 1px dashed rgba(var(--color-brand-700-rgb), 0.4);
  transition:
    color 180ms var(--ease-snappy),
    border-color 180ms var(--ease-snappy);

  &:hover {
    color: var(--color-brand-600);
    border-bottom-color: var(--color-brand-500);
  }
`;

// ─── Footer ─────────────────────────────────────────────────────────────────

export const LegalFooter = styled.div`
  margin-top: 22px;
  padding: 22px 18px 6px;
  text-align: center;
  font-family: var(--font-family-primary);
  font-size: 12px;
  font-weight: 500;
  line-height: normal;
  color: var(--color-text-muted);
  border-top: 1px solid var(--color-line);
`;
